import {
  logEvent,
  setUserId,
  setUserProperties,
  type Analytics,
} from "firebase/analytics";

import { getFirebaseAnalytics } from "../firebaseConfig";
import { LoggedUser } from "../models/Person";
import { store } from "../store/store";

export type AnalyticsParams = Record<string, string | number | boolean | undefined>;

const LOGIN_SESSION_KEY = "ga4_login_user";

export function inferTenantIdFromHostname(
  hostname: string = typeof window !== "undefined" ? window.location.hostname : ""
): string {
  const host = hostname.toLowerCase();
  if (host.includes("take-the-lead") || host.includes("takethelead")) return "take-the-lead";
  if (host.includes("mt-demo") || host.includes("werule-demo")) return "werule-demo";
  if (host.includes("werule-ai-intake")) return "werule-ai-intaketest";
  if (host.includes("women.nyc") || host.includes("womennyc")) return "nyc";
  if (host.includes("test.weruledev.com")) return "nyc";
  return "werule";
}

export function getTenantId(): string {
  try {
    const fromConfig = store.getState().config.tenantId;
    if (fromConfig) {
      return fromConfig;
    }
  } catch {
    // Store not ready (tests)
  }
  return inferTenantIdFromHostname();
}

export function analyticsUserRole(user: {
  isAdmin?: boolean;
  isGuest?: boolean;
  isMentor?: boolean;
}): "admin" | "guest" | "mentor" | "mentee" {
  if (user.isAdmin) return "admin";
  if (user.isGuest) return "guest";
  if (user.isMentor) return "mentor";
  return "mentee";
}

export function viewedUserRole(user: {
  isGuest?: boolean;
  isMentor?: boolean;
}): "mentor" | "mentee" | "guest" {
  if (user.isMentor) return "mentor";
  if (user.isGuest) return "guest";
  return "mentee";
}

export function signupSourceFromValue(source?: string | null): string {
  if (source === "qr_registration" || source === "qr_profile" || source === "qr_booking") {
    return source;
  }
  return "organic";
}

function compactParams(params?: AnalyticsParams): Record<string, string | number | boolean> {
  const payload: Record<string, string | number | boolean> = {
    tenant_id: getTenantId(),
  };
  if (!params) {
    return payload;
  }
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== "") {
      payload[key] = value;
    }
  }
  return payload;
}

function withAnalytics(run: (analytics: Analytics) => void) {
  getFirebaseAnalytics((analytics) => {
    try {
      run(analytics);
    } catch (error) {
      console.warn("Analytics event failed:", error);
    }
  });
}

export function trackEvent(name: string, params?: AnalyticsParams) {
  withAnalytics((analytics) => {
    logEvent(analytics, name, compactParams(params));
  });
}

export function trackSignUp(options: {
  method: string;
  role: string;
  source?: string | null;
}) {
  trackEvent("sign_up", {
    method: options.method,
    role: options.role,
    signup_source: signupSourceFromValue(options.source),
  });
}

export function setAnalyticsUser(user: LoggedUser) {
  withAnalytics((analytics) => {
    setUserId(analytics, String(user.id));
    setUserProperties(analytics, {
      tenant_id: getTenantId(),
      user_role: analyticsUserRole(user),
    });
  });
}

export function clearAnalyticsUser() {
  withAnalytics((analytics) => {
    setUserId(analytics, null);
    setUserProperties(analytics, {
      tenant_id: getTenantId(),
      user_role: "anonymous",
    });
  });
}

export function markLoginSent(userId: number) {
  try {
    sessionStorage.setItem(LOGIN_SESSION_KEY, String(userId));
  } catch {
    // Ignore private-mode sessionStorage failures
  }
}

export function loginAlreadySent(userId: number): boolean {
  try {
    return sessionStorage.getItem(LOGIN_SESSION_KEY) === String(userId);
  } catch {
    return false;
  }
}

export function clearLoginSent() {
  try {
    sessionStorage.removeItem(LOGIN_SESSION_KEY);
  } catch {
    // Ignore
  }
}
